/**
 * Aktivitäts-Feed-Logik für die C3-Verlauf-Sidebar (reine Helfer, kein React/i18n).
 *
 * Klopft Content-Block-Versionen (Master- + Channel-Scopes) zu einem chronologischen
 * Feed flach. Badge aus source_type (BEARBEITET/KI/SYNC/ERSTELLT). „Nur Connector"
 * = channel-scoped Einträge (channelId != null).
 */

import type { ContentBlockVersion } from '@/types/versioning'
import type { BlockType } from '@/types/content-blocks'

export type ActivityBadge = 'edited' | 'ai' | 'sync' | 'created'

export interface BlockRef {
  blockId: string
  blockType: BlockType
  /** Channel-Package-ID (= Editor-Scope-Key) oder null = Master/Produkt */
  scopeKey: string | null
  channelName: string
}

export interface ActivityEntry {
  id: string
  blockId: string
  versionId: string
  blockType: BlockType
  scopeKey: string | null
  channelName: string
  isConnector: boolean
  badge: ActivityBadge
  versionNumber: number
  timestamp: string
  summary: string
}

export function badgeFor(version: ContentBlockVersion): ActivityBadge {
  if (version.version_number === 1) return 'created'
  switch (version.source_type) {
    case 'regenerate':
      return 'ai'
    case 'manual_publish':
    case 'scheduled_restore':
    case 'rollback':
      return 'sync'
    default:
      return 'edited'
  }
}

/** Avatar-Buchstabe + Tailwind-Klassen je Badge (User/KI/System) für den Versionslog. */
export function avatarFor(badge: ActivityBadge): { letter: string; cls: string } {
  switch (badge) {
    case 'ai':
      return { letter: '★', cls: 'bg-amber-500 text-white' }
    case 'sync':
      return { letter: 'S', cls: 'bg-neutral-900 text-white dark:bg-neutral-700' }
    case 'created':
      return { letter: '+', cls: 'bg-emerald-600 text-white' }
    default:
      return { letter: 'A', cls: 'bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100' }
  }
}

/** Flacht Versionen je Block zu sortierten Einträgen (neueste zuerst). */
export function buildEntries(refs: BlockRef[], versionsByBlock: Record<string, ContentBlockVersion[]>): ActivityEntry[] {
  const entries: ActivityEntry[] = []
  for (const ref of refs) {
    for (const v of versionsByBlock[ref.blockId] ?? []) {
      entries.push({
        id: `${ref.blockId}-${v.id}`,
        blockId: ref.blockId,
        versionId: v.id,
        blockType: ref.blockType,
        scopeKey: ref.scopeKey,
        channelName: ref.channelName,
        isConnector: ref.scopeKey != null,
        badge: badgeFor(v),
        versionNumber: v.version_number,
        timestamp: v.created_at,
        summary: v.diff_summary ?? '',
      })
    }
  }
  return entries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
}

export interface ActivityGroup {
  /** 'thisWeek' | 'thisMonth' | 'YYYY-MM' */
  key: string
  entries: ActivityEntry[]
}

/** Gruppiert nach Zeit: diese Woche, dieser Monat, sonst je Kalendermonat. */
export function groupEntries(entries: ActivityEntry[], now: Date = new Date()): ActivityGroup[] {
  const weekAgo = now.getTime() - 7 * 86_400_000
  const groups: ActivityGroup[] = []
  const byKey = new Map<string, ActivityEntry[]>()
  for (const e of entries) {
    const d = new Date(e.timestamp)
    let key: string
    if (d.getTime() >= weekAgo) key = 'thisWeek'
    else if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) key = 'thisMonth'
    else key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    if (!byKey.has(key)) {
      byKey.set(key, [])
      groups.push({ key, entries: byKey.get(key)! })
    }
    byKey.get(key)!.push(e)
  }
  return groups
}
